/**
 * The tour state machine.
 *
 * Owns exactly four things: which step is current, driving the app to that
 * step's route, resolving its anchor (or degrading), and emitting telemetry.
 * Rendering lives in `TourLayer`; placement lives in `position.ts`; finding
 * elements lives in `anchor.ts`.
 *
 * Deliberate non-goal: there is **no "surface ready" protocol**. Subscribing to
 * `periodBootstrapReady` / `materializationNonce` / per-page loading state would
 * couple the tour to every page's fetch lifecycle and break whenever one adds a
 * request. Instead, *the anchor is the readiness signal* — a step is ready when
 * its element exists with a non-zero rect. That is definitionally true, needs no
 * cooperation from page components, and degrades through `fallback`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { buildCfoRoutePath, parseCfoRouteState } from '../parity-dev/cfo/cfoRoutes'
import { scrollAnchorIntoView, waitForAnchor, type AnchorMatch } from './anchor'
import { createTourBridge, type TourBridgeEvent } from './tourEvents'
import {
  TOUR_EVENTS,
  trackTour,
  type TourEventContext,
  type TourExitReason,
} from './tourTelemetry'
import {
  clearTourProgress,
  readTourProgress,
  writeTourProgress,
  type TourActivation,
} from './tourSession'
import {
  flattenTourSteps,
  tourStepCount,
  type LoadedTour,
  type TourRoute,
  type TourStepRef,
} from './tourTypes'

/** How the current step's anchor turned out. */
export type TourAnchorState = 'pending' | 'resolved' | 'fallback' | 'none'

export type TourEngine = {
  active: boolean
  finished: boolean
  current: TourStepRef | null
  stepCount: number
  anchorState: TourAnchorState
  anchorElement: HTMLElement | null
  /** Which selector actually matched — surfaced for the self-check report. */
  matchedKind: string | null
  next: () => void
  prev: () => void
  gotoStep: (stepId: string) => void
  end: (reason: TourExitReason) => void
  /** Advance from a lead-form step once contact details are captured. */
  completeLeadStep: (leadId?: string | null) => void
  emitCta: (variant: 'primary' | 'secondary' | 'convert', label: string) => void
  emitChecklistItem: (itemId: string, stepId: string) => void
  emitTheirNumbers: (props: Record<string, number | string | null>) => void
}

const BRIDGE_EVENT: Partial<Record<string, TourBridgeEvent>> = {
  [TOUR_EVENTS.open]: 'demo_open',
  [TOUR_EVENTS.stepView]: 'step_view',
  [TOUR_EVENTS.finished]: 'demo_finished',
  [TOUR_EVENTS.chapterView]: 'flow_start',
  [TOUR_EVENTS.leadIdentify]: 'lead_identify',
  [TOUR_EVENTS.ctaPrimary]: 'primary_cta',
  [TOUR_EVENTS.ctaSecondary]: 'secondary_cta',
  [TOUR_EVENTS.ctaConvert]: 'convert_cta',
}

/** Build the query string a step's route wants, preserving the tour params. */
function routeSearch(route: TourRoute, keep: URLSearchParams): string {
  const params = new URLSearchParams()
  keep.forEach((value, key) => {
    if (key.startsWith('fg_tour') || /^token\[/.test(key)) params.set(key, value)
  })
  Object.entries(route.params || {}).forEach(([key, value]) => params.set(key, value))
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export function useTourEngine(
  tour: LoadedTour | null,
  activation: TourActivation | null,
): TourEngine {
  const [index, setIndex] = useState(0)
  const [active, setActive] = useState(false)
  const [finished, setFinished] = useState(false)
  // Distinct from `active`: without it the start effect below sees an inactive,
  // unfinished tour and immediately restarts the one the visitor just closed.
  const [dismissed, setDismissed] = useState(false)
  const [anchorState, setAnchorState] = useState<TourAnchorState>('pending')
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null)
  const [matchedKind, setMatchedKind] = useState<string | null>(null)

  const stepEnteredAt = useRef<number>(0)
  const finishRef = useRef<(() => void) | null>(null)
  const completed = useRef<Set<string>>(new Set())
  const lastChapterId = useRef<string | null>(null)
  const openTracked = useRef(false)

  const steps = useMemo<TourStepRef[]>(() => (tour ? flattenTourSteps(tour.doc) : []), [tour])
  const stepCount = tour ? tourStepCount(tour.doc) : 0
  const current = steps[index] || null

  const bridge = useMemo(() => createTourBridge(activation?.embedOrigin || null), [activation?.embedOrigin])

  const ctx = useMemo<TourEventContext>(() => ({
    tour_slug: tour?.doc.slug || '',
    tour_version: tour?.version ?? 0,
    tour_source: tour?.source || 'bundled',
    context: tour?.doc.context || 'demo',
    embed: Boolean(activation?.embed),
    step_count: stepCount,
    industry: tour?.doc.entry.company || null,
    variant: activation?.variant || null,
  }), [tour, activation?.embed, activation?.variant, stepCount])

  /** One call emits both the first-party event and the cross-frame mirror. */
  const emit = useCallback((event: string, props: Record<string, unknown> = {}) => {
    if (!tour) return
    trackTour(event as never, ctx, props)
    const bridgeEvent = BRIDGE_EVENT[event]
    if (bridgeEvent) {
      bridge.post({
        event: bridgeEvent,
        demo: { id: tour.doc.slug, name: tour.doc.name, url: window.location.href },
        step: typeof props.step_id === 'string'
          ? { id: props.step_id, index: Number(props.step_index) || 0 }
          : undefined,
        flow: typeof props.chapter_id === 'string'
          ? { id: String(props.chapter_id), name: String(props.chapter_title || props.chapter_id) }
          : undefined,
        lead: bridgeEvent === 'lead_identify' ? { identified: true } : undefined,
        cta: typeof props.cta_label === 'string' ? { label: props.cta_label } : undefined,
      })
    }
  }, [bridge, ctx, tour])

  // ---- start / resume ----------------------------------------------------
  useEffect(() => {
    if (!tour || active || finished || dismissed) return
    const saved = readTourProgress(tour.doc.slug, tour.version)
    const resumeAt = saved ? steps.findIndex((s) => s.step.id === saved.stepId) : -1
    if (saved && resumeAt >= 0) {
      completed.current = new Set(saved.completedStepIds)
      setIndex(resumeAt)
    }
    setActive(true)
    if (!openTracked.current) {
      openTracked.current = true
      emit(TOUR_EVENTS.open, { resumed: resumeAt >= 0 })
    }
  }, [tour, active, finished, dismissed, steps, emit])

  // ---- drive the app to the current step ---------------------------------
  useEffect(() => {
    if (!active || !current || !tour) return
    const controller = new AbortController()
    const { signal } = controller
    setAnchorState('pending')
    setAnchorElement(null)
    setMatchedKind(null)
    stepEnteredAt.current = Date.now()

    const run = async () => {
      const { step, chapter } = current

      // 1. Navigate. `pushState` + `fg_route_change`, never `location.href` —
      //    a full reload would discard the engine and restart the tour.
      //
      //    Params count as navigation. The CEO cockpit routes through surfaces
      //    on the SAME page (`fg_ceo_surface=explore`), so "already on /cfo/ceo"
      //    does not mean "already where the step points" — checking only the
      //    page left the anchored panels unrendered and every cockpit step
      //    degrading to a modal.
      //    Client-workspace tours drive a different shell: pages are React
      //    state, not URLs. The workspace stamps its current page on
      //    [data-fg-workspace-page] and listens for fg_workspace_goto — the
      //    engine speaks that protocol instead of pushState when the doc's
      //    context is 'client'.
      if (tour?.doc.context === 'client') {
        const wsRoot = document.querySelector('[data-fg-workspace-page]')
        const wsPage = wsRoot?.getAttribute('data-fg-workspace-page') || null
        if (wsRoot && wsPage !== step.route.page) {
          window.dispatchEvent(new CustomEvent('fg_workspace_goto', { detail: { page: step.route.page } }))
        }
      }
      const nonCfoSurface = tour?.doc.context === 'client' || tour?.doc.context === 'firm'
      const routeState = nonCfoSurface ? null : parseCfoRouteState(window.location)
      const currentParams = new URLSearchParams(window.location.search)
      const paramsDiffer = Object.entries(step.route.params || {}).some(
        ([key, value]) => currentParams.get(key) !== value,
      )
      if (routeState && (routeState.page !== step.route.page || paramsDiffer)) {
        const path = buildCfoRoutePath({
          siteSlug: routeState.siteSlug,
          workbookId: routeState.workbookId,
          // routeState is null for context:'client' docs, so this branch only
          // ever sees CFO pages — the cast records that invariant.
          page: step.route.page as import('../parity-dev/cfo/cfoRoutes').CfoPageSlug,
        })
        const search = routeSearch(step.route, new URLSearchParams(window.location.search))
        window.history.pushState(window.history.state, '', `${path}${search}`)
        window.dispatchEvent(new Event('fg_route_change'))
      }
      if (signal.aborted) return

      // 2. Chapter boundary telemetry, before the step view.
      if (lastChapterId.current !== chapter.id) {
        lastChapterId.current = chapter.id
        emit(TOUR_EVENTS.chapterView, { chapter_id: chapter.id, chapter_title: chapter.title })
      }

      // 3. Click a segmented control if the step asks for one.
      if (step.route.segment) {
        const match = await waitForAnchor(
          { kind: 'segment', control: step.route.segment.control, value: step.route.segment.value, label: 'segment' },
          { signal, timeoutMs: 4000 },
        )
        match?.element.click()
      }
      if (signal.aborted) return

      // 4. Resolve the anchor, or degrade. A missing element is a downgrade,
      //    never a broken overlay.
      if (!step.anchor) {
        setAnchorState('none')
      } else {
        const match: AnchorMatch | null = await waitForAnchor(step.anchor, { signal })
        if (signal.aborted) return
        if (match) {
          setAnchorElement(match.element)
          setMatchedKind(match.matchedKind)
          setAnchorState('resolved')
          await scrollAnchorIntoView(match.element, signal)
        } else {
          setAnchorState('fallback')
          emit(TOUR_EVENTS.anchorMissed, {
            step_id: step.id,
            step_index: current.index,
            chapter_id: chapter.id,
            anchor_id: step.anchor.label,
            anchor_kind: step.anchor.kind,
            fallback: step.fallback,
          })
          if (step.fallback === 'skip') {
            // Clamping to the last index would leave the tour parked on a step
            // whose anchor will never appear. If there is nothing after it,
            // the tour is over.
            if (current.index >= steps.length - 1) {
              finishRef.current?.()
            } else {
              setIndex(current.index + 1)
            }
            return
          }
        }
      }
      if (signal.aborted) return

      emit(TOUR_EVENTS.stepView, {
        step_id: step.id,
        step_index: current.index,
        chapter_id: chapter.id,
        chapter_title: chapter.title,
      })
      writeTourProgress({
        slug: tour.doc.slug,
        version: tour.version,
        stepId: step.id,
        completedStepIds: [...completed.current],
        startedAt: stepEnteredAt.current,
      })
    }

    void run()
    return () => controller.abort()
  }, [active, current, tour, steps.length, emit])

  // ---- advance conditions ------------------------------------------------
  useEffect(() => {
    if (!active || !current || anchorState === 'pending') return
    const { step } = current
    const controller = new AbortController()

    if (step.advance.kind === 'click_anchor' && anchorElement) {
      anchorElement.addEventListener('click', () => advance('anchor_click'), {
        once: true,
        signal: controller.signal,
      })
    } else if (step.advance.kind === 'timeout') {
      const timer = setTimeout(() => advance('timer'), step.advance.ms)
      controller.signal.addEventListener('abort', () => clearTimeout(timer))
    } else if (step.advance.kind === 'element_appears') {
      void waitForAnchor(step.advance.anchor, { signal: controller.signal }).then((match) => {
        if (match && !controller.signal.aborted) advance('element_appears')
      })
    }
    return () => controller.abort()
    // `advance` is stable via useCallback below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, current, anchorState, anchorElement])

  const finish = useCallback(() => {
    if (!tour || !current) return
    emit(TOUR_EVENTS.finished, {
      step_id: current.step.id,
      step_index: current.index,
      chapter_id: current.chapter.id,
      completed_steps: completed.current.size,
    })
    clearTourProgress(tour.doc.slug)
    setFinished(true)
    setActive(false)
  }, [tour, current, emit])

  useEffect(() => { finishRef.current = finish }, [finish])

  const advance = useCallback((method: string) => {
    if (!current) return
    completed.current.add(current.step.id)
    emit(TOUR_EVENTS.stepComplete, {
      step_id: current.step.id,
      step_index: current.index,
      chapter_id: current.chapter.id,
      dwell_ms: Math.max(0, Date.now() - stepEnteredAt.current),
      method,
    })
    if (current.index >= steps.length - 1) {
      finish()
      return
    }
    setIndex(current.index + 1)
  }, [current, steps.length, emit, finish])

  const next = useCallback(() => advance('button'), [advance])

  const prev = useCallback(() => {
    if (!current || current.index === 0) return
    emit(TOUR_EVENTS.stepBack, {
      step_id: current.step.id,
      step_index: current.index,
      chapter_id: current.chapter.id,
    })
    setIndex(current.index - 1)
  }, [current, emit])

  const gotoStep = useCallback((stepId: string) => {
    const target = steps.findIndex((s) => s.step.id === stepId)
    if (target >= 0) setIndex(target)
  }, [steps])

  const end = useCallback((reason: TourExitReason) => {
    if (!tour || !current) return
    emit(TOUR_EVENTS.exit, {
      step_id: current.step.id,
      step_index: current.index,
      chapter_id: current.chapter.id,
      exit_reason: reason,
      dwell_ms: Math.max(0, Date.now() - stepEnteredAt.current),
      completed_steps: completed.current.size,
    })
    clearTourProgress(tour.doc.slug)
    setDismissed(true)
    setActive(false)
  }, [tour, current, emit])

  const completeLeadStep = useCallback((leadId?: string | null) => {
    emit(TOUR_EVENTS.leadIdentify, {
      step_id: current?.step.id,
      step_index: current?.index,
      lead_id: leadId || null,
    })
    advance('lead_form')
  }, [current, emit, advance])

  const emitCta = useCallback((variant: 'primary' | 'secondary' | 'convert', label: string) => {
    const event =
      variant === 'convert' ? TOUR_EVENTS.ctaConvert
      : variant === 'secondary' ? TOUR_EVENTS.ctaSecondary
      : TOUR_EVENTS.ctaPrimary
    emit(event, {
      step_id: current?.step.id,
      step_index: current?.index,
      chapter_id: current?.chapter.id,
      cta: variant,
      cta_label: label,
    })
  }, [current, emit])

  const emitChecklistItem = useCallback((itemId: string, stepId: string) => {
    emit(TOUR_EVENTS.checklistItem, {
      checklist_item_id: itemId,
      target_step_id: stepId,
      step_id: current?.step.id,
      step_index: current?.index,
    })
  }, [emit, current])

  const emitTheirNumbers = useCallback((props: Record<string, number | string | null>) => {
    emit(TOUR_EVENTS.theirNumbers, { ...props, step_id: current?.step.id })
  }, [emit, current])

  // ---- keyboard ----------------------------------------------------------
  useEffect(() => {
    if (!active) return
    const controller = new AbortController()
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.defaultPrevented) return
      const target = e.target as HTMLElement | null
      // Never steal keys from a field the visitor is typing in.
      if (target && /^(input|textarea|select)$/i.test(target.tagName)) return
      if (e.key === 'Escape') { e.preventDefault(); end('escape') }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next() }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev() }
    }, { signal: controller.signal })
    return () => controller.abort()
  }, [active, end, next, prev])

  // ---- browser back ------------------------------------------------------
  useEffect(() => {
    if (!active || !current) return
    const controller = new AbortController()
    window.addEventListener('popstate', () => {
      // Don't fight the back button: if the visitor navigated off the step's
      // page, walk back to the last step that matches where they now are.
      const state = parseCfoRouteState(window.location)
      if (!state || state.page === current.step.route.page) return
      const back = [...steps].reverse().find((s) => s.step.route.page === state.page)
      if (back) setIndex(back.index)
    }, { signal: controller.signal })
    return () => controller.abort()
  }, [active, current, steps])

  return {
    active,
    finished,
    current,
    stepCount,
    anchorState,
    anchorElement,
    matchedKind,
    next,
    prev,
    gotoStep,
    end,
    completeLeadStep,
    emitCta,
    emitChecklistItem,
    emitTheirNumbers,
  }
}
